import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { correctiveActionApi } from "../../services/correctiveActionApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { statusLabel } from "../../lib/statusLabel";
import { useLocalization } from "../../hooks/useLocalization";

const STATUSES = [
  "OPEN", "PLAN_REQUIRED", "IN_PROGRESS", "EVIDENCE_PENDING", "MANAGEMENT_REVIEW",
  "READY_TO_SUBMIT", "SUBMITTED", "FOLLOW_UP_REQUIRED", "CLOSED", "OVERDUE",
];
const EMPTY_FORM = { title: "", legal_provision: "", finding: "", labor_office_section: "", required_correction: "", responsible_owner: "", target_date: "" };

export default function CorrectiveActionsPage() {
  const { t } = useLocalization();
  const { canViewCorrectiveAction, canManageCorrectiveAction } = usePermissions();
  const [cases, setCases] = useState([]);
  const [status, setStatus] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    correctiveActionApi.listCases(status).then((r) => setCases(r.data)).catch(() => {});
  }, [status]);
  useEffect(() => { if (canViewCorrectiveAction) load(); }, [canViewCorrectiveAction, load]);

  if (!canViewCorrectiveAction) return <p className="text-sm text-gray-500" data-testid="corrective-action-access-denied">{t("common.accessDenied")}</p>;

  const createCase = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    setBusy(true);
    try {
      await correctiveActionApi.createCase({ ...form, target_date: form.target_date || null });
      toast.success(t("mgmtCorrectiveAction.caseCreated"));
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="corrective-actions-page">
      <PageHelpBanner pageKey="corrective-actions" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtCorrectiveAction.listHelp") }} />
      </PageHelpBanner>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtCorrectiveAction.title")}</h2>
          <p className="text-sm text-gray-500">{t("mgmtCorrectiveAction.subtitle")}</p>
        </div>
        {canManageCorrectiveAction && (
          <button data-testid="corrective-action-new-case-btn" onClick={() => setShowForm((s) => !s)} className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">
            {showForm ? t("common.cancel") : `+ ${t("mgmtCorrectiveAction.newCase")}`}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={createCase} className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="corrective-action-new-case-form">
          <input data-testid="ca-title-input" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t("mgmtCorrectiveAction.titleRequired")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <input data-testid="ca-legal-provision-input" value={form.legal_provision} onChange={(e) => setForm((f) => ({ ...f, legal_provision: e.target.value }))} placeholder={t("mgmtCorrectiveAction.legalProvision")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="ca-labor-office-section-input" value={form.labor_office_section} onChange={(e) => setForm((f) => ({ ...f, labor_office_section: e.target.value }))} placeholder={t("mgmtCorrectiveAction.laborOfficeSection")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <textarea data-testid="ca-finding-input" value={form.finding} onChange={(e) => setForm((f) => ({ ...f, finding: e.target.value }))} placeholder={t("mgmtCorrectiveAction.finding")} rows={2} className="border border-gray-200 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <textarea data-testid="ca-required-correction-input" value={form.required_correction} onChange={(e) => setForm((f) => ({ ...f, required_correction: e.target.value }))} placeholder={t("mgmtCorrectiveAction.requiredCorrection")} rows={2} className="border border-gray-200 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <input data-testid="ca-responsible-owner-input" value={form.responsible_owner} onChange={(e) => setForm((f) => ({ ...f, responsible_owner: e.target.value }))} placeholder={t("mgmtCorrectiveAction.responsibleOwner")} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <input data-testid="ca-target-date-input" type="date" value={form.target_date} onChange={(e) => setForm((f) => ({ ...f, target_date: e.target.value }))} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          <button data-testid="ca-create-case-btn" disabled={busy || !form.title} type="submit" className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 md:col-span-2">
            {busy ? t("mgmtHealthCommittee.creatingEllipsis") : t("mgmtCorrectiveAction.createCase")}
          </button>
        </form>
      )}

      <div className="flex gap-2 flex-wrap">
        <button data-testid="ca-status-filter-ALL" onClick={() => setStatus("")} className={`px-2.5 py-1 rounded-md text-xs font-medium border ${!status ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>{t("mgmtLongHourInterview.all")}</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`ca-status-filter-${s}`} onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-md text-xs font-medium border ${status === s ? "bg-gray-800 text-white border-gray-800" : "border-gray-200 bg-white hover:bg-gray-50 text-gray-600"}`}>
            {statusLabel(t, s)}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtCorrectiveAction.colCode")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colTitle")}</th><th className="px-4 py-3 font-medium">{t("mgmtHealthCommittee.colOwner")}</th><th className="px-4 py-3 font-medium">{t("mgmtCorrectiveAction.colTarget")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {cases.map((c) => (
              <tr key={c.id} className="border-t border-gray-100" data-testid={`ca-case-row-${c.id}`}>
                <td className="px-4 py-2 text-gray-500 text-xs font-mono">{c.code}</td>
                <td className="px-4 py-2 text-gray-700">{c.title}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{c.responsible_owner || "-"}</td>
                <td className="px-4 py-2 text-gray-500 text-xs">{c.target_date || "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-2"><Link to={`/management/compliance/corrective-actions/${c.id}`} className="text-xs font-semibold text-blue-600 hover:underline" data-testid={`ca-case-view-${c.id}`}>{t("mgmtLongHourInterview.view")}</Link></td>
              </tr>
            ))}
            {cases.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-ca-cases">{t("mgmtCorrectiveAction.noCasesFilter")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
